// Custom type definitions for IDSeat database
export type AppRole = 'idseat_admin' | 'club_admin';
export type QuestionType = 'multiple_choice' | 'nps' | 'text';
export type ScanSource = 'qr' | 'nfc' | 'screen' | 'unknown';
export type PortalType = 'football' | 'handball' | 'ice_hockey' | 'basketball' | 'concert' | 'other';

export type StripeConnectStatus =
  | 'not_started'
  | 'onboarding_started'
  | 'onboarding_incomplete'
  /** Hosted form finished, nothing due, Stripe reviewing — no action needed. */
  | 'pending_verification'
  | 'active'
  | 'restricted'
  | 'disabled';

/**
 * What Stripe is waiting for on the club's connected account. Mirror of
 * ConnectSnapshot in supabase/functions/_shared/stripe-connect-status.ts (the
 * app bundle cannot import from supabase/functions, so the shape is repeated).
 * Stored on clubs.stripe_requirements by the webhook and the status function.
 */
export interface StripeRequirementsSnapshot {
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  disabled_reason: string | null;
  currently_due: string[];
  past_due: string[];
  pending_verification: string[];
  eventually_due: string[];
  errors: { code: string; reason: string; requirement: string }[];
  /** ISO timestamp, or null when Stripe set no deadline. */
  current_deadline: string | null;
  /** A Stripe decision (rejection, review, listing) the club cannot fix in the hosted form. */
  review_blocked: boolean;
  /** ISO timestamp of the Stripe read this came from. */
  synced_at: string;
}

export interface Club {
  id: string;
  name: string;
  portal_type: PortalType;
  sportsmonks_team_id?: number | null;
  external_provider?: string | null;
  external_team_id?: string | null;
  stripe_account_id?: string | null;
  stripe_connect_status: StripeConnectStatus;
  commission_rate: number;
  stripe_onboarding_completed_at?: string | null;
  /** Set when this club borrows another club's Stripe account (id + status mirrored from it). */
  stripe_account_owner_club_id?: string | null;
  /** Last Stripe requirements read; null until the first sync after 2026-09-22. */
  stripe_requirements?: StripeRequirementsSnapshot | null;
  created_at: string;
}

export interface Stadium {
  id: string;
  name: string;
  /** URL segment for the fan page: go.idseat.com/<slug>/<tag_public_id>. */
  slug: string;
  timezone: string;
  created_at: string;
}

export interface StadiumClub {
  id: string;
  stadium_id: string;
  club_id: string;
  is_home_stadium: boolean;
  created_at: string;
}

export type EventKind = 'sports_match' | 'concert' | 'other';

export interface Event {
  id: string;
  club_id: string;
  stadium_id: string;
  name: string;
  kickoff_datetime: string;
  event_kind: EventKind;
  home_team_name?: string | null;
  home_team_logo_url?: string | null;
  away_team_name?: string | null;
  away_team_logo_url?: string | null;
  sportsmonks_fixture_id?: number | null;
  sportsmonks_league_id?: number | null;
  sportsmonks_season_id?: number | null;
  external_provider?: string | null;
  external_fixture_id?: string | null;
  // Concert / non-sport optional fields
  headliner_name?: string | null;
  support_acts?: string[] | null;
  doors_open_datetime?: string | null;
  poster_image_url?: string | null;
  created_at: string;
}

export interface Seat {
  id: string;
  stadium_id: string;
  stand: string;
  section: string;
  row: string;
  seat_number: string;
  display_label: string;
  created_at: string;
}

export type TagKind = 'seat' | 'screen';

export interface Tag {
  id: string;
  tag_public_id: string;
  nfc_uid: string | null;
  qr_code_id: string | null;
  /** null for a screen tag (kind = 'screen'). */
  seat_id: string | null;
  kind: TagKind;
  /** Screen tags only: what the club calls it ("Big screen", "Away end"). */
  label: string | null;
  stadium_id: string | null;
  club_id: string | null;
  collection_point_id: string | null;
  enabled: boolean;
  created_at: string;
}

export interface ClubSettings {
  id: string;
  club_id: string;
  cta_enabled: boolean;
  cta_text: string;
  cta_url: string | null;
  predictor_enabled: boolean;
  survey_enabled: boolean;
  /** Legacy single logo — mirror of the first sponsor_logos entry (receipts read it). */
  sponsor_logo_url: string | null;
  /** Sponsor logos shown on the fan page, rotating when more than one. */
  sponsor_logos?: { url: string }[] | null;
  /** Fan-page sponsor logo size, percent of the 40px base (50–200). */
  sponsor_logo_size?: number;
  stadium_image_url: string | null;
  primary_color: string;
  secondary_color: string;
  font_family: string;
  custom_font_url: string | null;
  /** When false, the entire Match Analytics ActionButton is hidden on the fan
   *  page (no Timeline, Stats, Lineup or Standings tab). Previously named
   *  lineups_enabled — renamed to match the fan-facing label. */
  match_analytics_enabled: boolean;
  ordering_enabled: boolean;
  /** Temporarily blocks NEW fan orders while the ordering block stays visible.
   *  Independent of ordering_enabled (which hides the block entirely). */
  ordering_paused: boolean;
  /** When true the fan checkout offers a half-time pre-order pickup slot. */
  preorder_enabled?: boolean;
  /** Minute of the match (first-half clock) after which pre-order checkout closes. */
  preorder_cutoff_minute?: number;
  /** False = no normal pick-up order; pick-up only as a half-time pre-order (missing = enabled). */
  pickup_now_enabled?: boolean;
  privacy_policy_url: string | null;
  terms_conditions_url: string | null;
  predictor_icon_url: string | null;
  survey_icon_url: string | null;
  lineups_icon_url: string | null;
  default_language_code: string;
  footer_text_color: string;
  predictor_close_at: 'kickoff' | 'half_time';
  payment_mode: PaymentMode;
  service_fee_feature_enabled: boolean;
  service_fee_cents: number;
  /** Ordered fan-page block keys (header, cta, predictor, survey, match_analytics,
   *  ordering). NULL falls back to the default order. See src/lib/fanPageBlocks.ts. */
  block_order: string[] | null;
  /** Optional short club-authored notice shown under the standard copy on the
   *  fan order-received screens. NULL/'' renders nothing. This column is the
   *  on/off switch and the source text; per-locale wording lives in
   *  club_translations under `ordering.orderReceivedNotice`. */
  order_received_notice: string | null;
  /** When false the fan checkout hides the free-text comments box.
   *  Absent on pre-migration rows — treat as true (historical behaviour). */
  order_comments_enabled?: boolean;
  /** Order giveaway: when enabled (and a prize is set) the fan checkout
   *  offers an optional email field. See src/lib/giveaway.ts. */
  giveaway_enabled?: boolean;
  giveaway_prize?: string | null;
  /** When true the fan page renders ONLY the takeover screen — every block and
   *  sub-view is suppressed. Message and button label fall back to the English
   *  defaults in TagPage when null. */
  fan_page_disabled: boolean;
  fan_page_disabled_message: string | null;
  fan_page_disabled_cta_label: string | null;
  /** Bumped by a DB trigger whenever a row in club_translations for this club
   *  changes (insert/update/delete). The fan page's useTranslation hook
   *  watches this via the existing club_settings realtime merge to refetch
   *  translations live. */
  translations_updated_at?: string | null;
  updated_at: string;
}

export interface CtaButton {
  id: string;
  club_id: string;
  button_text: string;
  button_url: string | null;
  icon_url: string | null;
  /** Preset icon key (src/lib/ctaIcons.ts); takes precedence over icon_url. */
  icon?: string | null;
  sort_order: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface Prize {
  id: string;
  club_id: string;
  name: string;
  sort_order: number;
  enabled: boolean;
  feature: string;
  created_at: string;
  updated_at: string;
}

/** A kiosk: the merchant unit at one venue, owned by an organization. */
export interface Operator {
  id: string;
  organization_id: string;
  stadium_id: string;
  name: string;
  kind: 'club_owned' | 'caterer' | 'food_truck';
  enabled: boolean;
  commission_rate_override: number | null;
  created_at: string;
  updated_at: string;
}

/** The kiosk's ordering switches (the same columns used to live on club_settings). */
export interface OperatorSettings {
  operator_id: string;
  payment_mode: PaymentMode;
  service_fee_feature_enabled: boolean;
  service_fee_cents: number;
  ordering_paused: boolean;
  order_received_notice: string | null;
  giveaway_enabled: boolean;
  giveaway_prize: string | null;
  order_comments_enabled: boolean;
  preorder_enabled: boolean;
  preorder_cutoff_minute: number;
  pickup_now_enabled: boolean;
  updated_at: string;
}

/** The kiosk serving a fan, as returned by get_fan_page_data. */
export interface FanOperator {
  id: string;
  name: string;
  kind: Operator['kind'];
  organization_id: string;
  organization_name: string;
  /** 'override' = set on the tenancy (its own kiosk or a caterer it picked); 'default' = the venue's caterer. */
  source: 'override' | 'default';
  /** The kiosk belongs to the club (its own, or its organization's). Absent from older payloads. */
  own?: boolean;
}

export interface MenuCategory {
  id: string;
  /** Tenant attribution (the organization's club); null for a kiosk without a club. */
  club_id: string | null;
  operator_id?: string | null;
  name: string;
  image_url: string | null;
  icon_name: string | null;
  sort_order: number;
  enabled: boolean;
  visible_sections: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface MenuItem {
  id: string;
  /** Tenant attribution (the organization's club); null for a kiosk without a club. */
  club_id: string | null;
  operator_id?: string | null;
  name: string;
  size: string;
  price: number;
  image_url: string | null;
  sort_order: number;
  enabled: boolean;
  /** false = pick-up only; undefined (pre-migration) counts as deliverable. */
  seat_delivery_enabled?: boolean;
  /** Tenants (club ids) whose events offer it; null/undefined = every tenant of the kiosk's venue. */
  tenant_club_ids?: string[] | null;
  category: string;
  category_id: string | null;
  addons?: any[] | null;
  vat_rate: number;
  /** Fixed vocabulary — see DIETARY_TAGS in src/lib/dietary.ts. */
  dietary_tags?: string[] | null;
  /** Fixed vocabulary — the EU-14 list, see EU_ALLERGENS in src/lib/dietary.ts. */
  allergens?: string[] | null;
  ingredients?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Survey {
  id: string;
  club_id: string;
  title: string;
  active: boolean;
  created_at: string;
}

export interface SurveyQuestion {
  id: string;
  survey_id: string;
  question_type: QuestionType;
  prompt: string;
  options_json: string[] | null;
  sort_order: number;
  required: boolean;
  /** multiple_choice only: fans may tick several options (answer stored as string[]). */
  allow_multiple?: boolean;
  created_at: string;
}

export interface Profile {
  id: string;
  user_id: string;
  role: AppRole;
  club_id: string | null;
  created_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
}

export interface InteractionCtaClick {
  id: string;
  club_id: string;
  stadium_id: string;
  event_id: string | null;
  seat_id: string;
  tag_id: string;
  occurred_at: string;
  user_agent: string | null;
  ip_hash: string | null;
}

export interface InteractionScorePrediction {
  id: string;
  club_id: string;
  stadium_id: string;
  event_id: string | null;
  seat_id: string;
  tag_id: string;
  occurred_at: string;
  home_score: number;
  away_score: number;
  email: string | null;
  marketing_consent: boolean;
}

export interface InteractionSurveyResponse {
  id: string;
  club_id: string;
  stadium_id: string;
  event_id: string | null;
  seat_id: string;
  tag_id: string;
  occurred_at: string;
  responses_json: Record<string, string | number>;
  email: string | null;
  marketing_consent: boolean;
}

export interface InteractionPageVisit {
  id: string;
  club_id: string;
  stadium_id: string;
  event_id: string | null;
  seat_id: string;
  tag_id: string;
  scan_source: ScanSource;
  occurred_at: string;
  user_agent: string | null;
  ip_hash: string | null;
}

// Event lineup data
export interface EventLineup {
  id: string;
  event_id: string;
  team_type: 'home' | 'away';
  team_name: string | null;
  team_logo_url: string | null;
  players_json: LineupPlayer[];
  formation: string | null;
  fetched_at: string;
  created_at: string;
}

export interface LineupPlayer {
  player_id: number;
  player_name: string;
  jersey_number: number;
  formation_position: number | null;
  position_id: number | null;
  /** Player headshot URL from SportMonks (optional; populated by sync-lineups). */
  image_url?: string | null;
  /** Match rating from SportMonks (0–10 scale). Only present after the match
   *  starts and SportMonks publishes the per-player rating. */
  rating?: number | null;
}

// Live score data
export type MatchStatus = 'not_started' | 'live' | 'half_time' | 'finished' | 'postponed' | 'cancelled';

export interface EventLiveScore {
  id: string;
  event_id: string;
  home_score: number;
  away_score: number;
  match_status: MatchStatus;
  minute: number | null;
  extra_info: string | null;
  last_updated: string;
  created_at: string;
}

// Match analytics — populated by sync-live-scores (events + stats) and sync-standings.

export interface MatchTimelineEvent {
  id: string;
  sportsmonks_event_id: number;
  minute: number | null;
  extra_minute: number | null;
  team_type: 'home' | 'away' | null;
  type_code: string;
  player_name: string | null;
  related_player_name: string | null;
  info: { result?: string | null; info?: string | null; type_id?: number | null } | null;
}

export interface MatchStatsRow {
  team_type: 'home' | 'away';
  stats: Record<string, number>;
  last_updated: string;
}

export interface LeagueStanding {
  sportsmonks_season_id: number;
  sportsmonks_team_id: number;
  team_name: string | null;
  team_logo_url: string | null;
  position: number | null;
  played: number | null;
  won: number | null;
  drawn: number | null;
  lost: number | null;
  goals_for: number | null;
  goals_against: number | null;
  goal_difference: number | null;
  points: number | null;
  recent_form: string | null;
}

export interface MatchAnalytics {
  event_id: string;
  season_id: number | null;
  club_team_id: number | null;
  events: MatchTimelineEvent[];
  stats: MatchStatsRow[];
  lineups: EventLineup[];
  standings: LeagueStanding[];
}

// i18n types
export interface SupportedLanguage {
  code: string;
  name: string;
  native_name: string;
  enabled: boolean;
  sort_order: number;
}

export interface CoreTranslation {
  id: string;
  locale: string;
  translation_key: string;
  translation_value: string;
  created_at: string;
  updated_at: string;
}

export interface ClubTranslation {
  id: string;
  club_id: string;
  locale: string;
  translation_key: string;
  translation_value: string;
  created_at: string;
  updated_at: string;
}

// Extended types for joined queries
export interface TagWithDetails extends Tag {
  /** The seat with its venue; null for a screen tag. */
  seat: (Seat & { stadium: Stadium }) | null;
  /** Screen tags carry their venue here instead (get_fan_page_data). */
  stadium?: Stadium | null;
}

/** The venue a tag belongs to, wherever the payload put it. */
export function tagStadium(tag: Pick<TagWithDetails, 'seat' | 'stadium'> | null | undefined): Stadium | null {
  return tag?.stadium ?? tag?.seat?.stadium ?? null;
}

export interface SurveyWithQuestions extends Survey {
  survey_questions: SurveyQuestion[];
}

// ── Live poll (man-of-the-match) ────────────────────────────────────────────
/** How a poll's options are sourced. `custom` = typed by hand; the rest are
 *  preset from real match data via the seed_poll_options RPC. */
export type PollCategory = 'custom' | 'motm' | 'goal_of_match' | 'match_winner';
export interface PollOption {
  id: string;
  poll_id: string;
  label: string;
  sort_order: number;
  // Enrichment — populated for MOTM starters; null for custom/subs/other.
  player_id?: number | null;
  image_url?: string | null;
  jersey_number?: number | null;
  team_type?: 'home' | 'away' | null;
  // NULL = hand-typed custom option; set = seeded for that one match.
  event_id?: string | null;
}
export interface Poll {
  id: string;
  club_id: string;
  title: string;
  status: 'draft' | 'open' | 'closed';
  category: PollCategory;
  options: PollOption[];
}
export interface PollResultRow {
  poll_id: string;
  option_id: string;
  event_id: string | null;
  vote_count: number;
}

export type OrderingMode = 'delivery' | 'pickup';
/** Section CONFIG mode — 'both' means the fan chooses at checkout. Orders
 *  always record the resolved two-valued OrderingMode (the same split as
 *  club_settings.payment_mode 'fan_choice' vs orders.payment_mode). */
export type SectionOrderingMode = OrderingMode | 'both';
export type OrderStatus = 'pending_payment' | 'scheduled' | 'pending' | 'delivered' | 'ready_at_collection_point' | 'collected' | 'cancelled';
export type PaymentStatus = 'unpaid' | 'pending_payment' | 'paid' | 'refunded' | 'failed' | 'partially_refunded';
export type PaymentMode = 'online_only' | 'in_person_only' | 'fan_choice';
export type OrderPaymentMode = 'online' | 'in_person';

export interface Payment {
  id: string;
  order_id: string;
  stripe_payment_intent_id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'partially_refunded';
  stripe_charge_id: string | null;
  refund_amount: number;
  idempotency_key: string;
  created_at: string;
  updated_at: string;
}

export interface CollectionPoint {
  id: string;
  /** Tenant attribution (the organization's club); null for a kiosk without a club. */
  club_id: string | null;
  operator_id?: string | null;
  name: string;
  instructions_text: string | null;
  instructions_image_url: string | null;
  phone_number: string | null;
  sort_order: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SectionOrderingConfig {
  id: string;
  club_id: string | null;
  operator_id?: string | null;
  section: string;
  ordering_mode: OrderingMode;
  collection_point_id: string | null;
  created_at: string;
  updated_at: string;
}
